import React from 'react';
import { PRODUCTS_LIST } from '../../../../shared/products';
import { PaymentMethods } from '../../../../shared/payments';
import {
    AdminAnalyticsData,
    AnalyticsGranularity,
} from '../../../../shared/admin.analytics';
import { SystemShared } from '../../../../shared/system';
import { langData } from '../../../../src/lang';
import { LangBlock } from '../../global';
import { WebStorage } from '../../../storage';
import {
    AdminButton,
    AdminCard,
    AdminGrid,
    AdminPageWrap,
    AdminSection,
    AdminStatCard,
} from './admin.ui';
import { AdminDataTable } from './admin.table';
import {
    AdminDelta,
    AdminDonutChart,
    AdminHBarList,
    AdminLineChart,
    CHART_COLORS,
    compactNumber,
    formatChartDate,
} from './admin.charts';

const DAY = 86400;

const PRESETS: { key: string; label: langData; seconds: number }[] = [
    { key: '7d', label: 'AdminPreset7d', seconds: 7 * DAY },
    { key: '30d', label: 'AdminPreset30d', seconds: 30 * DAY },
    { key: '90d', label: 'AdminPreset90d', seconds: 90 * DAY },
    { key: '1y', label: 'AdminPreset1y', seconds: 365 * DAY },
    { key: 'all', label: 'AdminPresetAll', seconds: 0 },
];

const GRANULARITY_OPTIONS: { key: AnalyticsGranularity | 'auto'; label: langData }[] = [
    { key: 'auto', label: 'AdminGranularityAuto' },
    { key: 'day', label: 'AdminGranularityDay' },
    { key: 'week', label: 'AdminGranularityWeek' },
    { key: 'month', label: 'AdminGranularityMonth' },
];

const toDateInput = (ts: number) => {
    const date = new Date(ts * 1000);

    return `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, '0')}-${`${date.getDate()}`.padStart(
        2,
        '0',
    )}`;
};

const fromDateInput = (value: string, endOfDay = false) => {
    const suffix = endOfDay ? 'T23:59:59' : 'T00:00:00';

    return Math.floor(new Date(`${value}${suffix}`).getTime() / 1000);
};

const money = (value: number) => `$${SystemShared.numberFormat(value)}`;

const paymentMethodName = (type: string) =>
    PaymentMethods.find((item) => item.type === type)?.name || type;

const BRANCH_LABELS: Record<string, langData> = {
    main: 'AdminBranchMain',
    beta: 'AdminBranchBeta',
    develop: 'AdminBranchDevelop',
};

const OS_LABELS: Record<string, langData> = {
    windows: 'AppDownloadPlatformWindows',
    linux: 'AppDownloadPlatformLinux',
    macos: 'AppDownloadPlatformMacos',
};

export const AdminAnalytics = () => {
    const [, , LangString] = LangBlock();
    const [preset, setPreset] = React.useState('30d');
    const [granularity, setGranularity] = React.useState<AnalyticsGranularity | 'auto'>('auto');
    const [from, setFrom] = React.useState(() => SystemShared.timestamp - 30 * DAY);
    const [to, setTo] = React.useState(() => SystemShared.timestamp);
    const [data, setData] = React.useState<AdminAnalyticsData | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState('');

    const productName = React.useCallback(
        (type: string) => {
            const cfg = PRODUCTS_LIST.find((item) => item.type === type);

            return cfg ? LangString(cfg.name) : type;
        },
        [LangString],
    );
    const branchLabel = React.useCallback(
        (id: string) => (BRANCH_LABELS[id] ? LangString(BRANCH_LABELS[id]) : id),
        [LangString],
    );
    const osLabel = React.useCallback(
        (id: string) => (OS_LABELS[id] ? LangString(OS_LABELS[id]) : id),
        [LangString],
    );

    const applyPreset = (key: string) => {
        const item = PRESETS.find((presetItem) => presetItem.key === key);

        if (!item) {
            return;
        }

        const now = SystemShared.timestamp;
        setPreset(key);
        setTo(now);
        setFrom(item.seconds === 0 ? 0 : now - item.seconds);
    };

    const load = React.useCallback(() => {
        setLoading(true);
        setError('');

        const query: { from: number; to: number; granularity?: AnalyticsGranularity } = {
            from,
            to,
        };

        if (granularity !== 'auto') {
            query.granularity = granularity;
        }

        WebStorage.ApiRequest('/api/admin/analytics', query).then((res) => {
            setLoading(false);

            if (res.status) {
                setData(res as unknown as AdminAnalyticsData);
            } else {
                setError(res.error_message || LangString('AdminAnalyticsLoadFailed'));
            }
        });
    }, [from, to, granularity, LangString]);

    React.useEffect(() => {
        load();
    }, [load]);

    const chartGranularity = data?.range.granularity || 'day';
    const formatX = React.useCallback(
        (t: number) => formatChartDate(t, chartGranularity),
        [chartGranularity],
    );
    const compare = data?.compare;
    const appDownloadOs = React.useMemo(() => {
        const map = new Map<string, number>();

        (data?.appDownloads || []).forEach((row) => {
            map.set(row.platform, (map.get(row.platform) || 0) + row.downloads);
        });

        return [...map.entries()]
            .map(([platform, downloads]) => ({ platform, downloads }))
            .sort((left, right) => right.downloads - left.downloads);
    }, [data]);
    const appDownloadBranches = React.useMemo(() => {
        const map = new Map<string, number>();

        (data?.appDownloads || []).forEach((row) => {
            map.set(row.branchId, (map.get(row.branchId) || 0) + row.downloads);
        });

        return [...map.entries()]
            .map(([branchId, downloads]) => ({ branchId, downloads }))
            .sort((left, right) => right.downloads - left.downloads);
    }, [data]);

    return (
        <AdminPageWrap>
            <AdminSection
                title={LangString('AdminAnalyticsTitle')}
                subtitle={LangString('AdminAnalyticsSubtitle')}
                actions={
                    <AdminButton variant="ghost" size="sm" onClick={load} disabled={loading}>
                        {loading ? LangString('AdminLoading') : LangString('AdminRefresh')}
                    </AdminButton>
                }
            >
                <div className="admin-analytics-toolbar">
                    <div className="admin-lang-tabs">
                        {PRESETS.map((item) => (
                            <button
                                key={item.key}
                                type="button"
                                className={`admin-lang-tabs__item${preset === item.key ? ' admin-lang-tabs__item--active' : ''}`}
                                onClick={() => applyPreset(item.key)}
                            >
                                {LangString(item.label)}
                            </button>
                        ))}
                    </div>
                    <div className="admin-analytics-toolbar__range">
                        <label>
                            {LangString('AdminFrom')}
                            <input
                                type="date"
                                value={from > 0 ? toDateInput(from) : ''}
                                onChange={(event) => {
                                    if (!event.target.value) {
                                        return;
                                    }

                                    setPreset('custom');
                                    setFrom(fromDateInput(event.target.value));
                                }}
                            />
                        </label>
                        <label>
                            {LangString('AdminTo')}
                            <input
                                type="date"
                                value={toDateInput(to)}
                                onChange={(event) => {
                                    if (!event.target.value) {
                                        return;
                                    }

                                    setPreset('custom');
                                    setTo(fromDateInput(event.target.value, true));
                                }}
                            />
                        </label>
                    </div>
                    <div className="admin-lang-tabs">
                        {GRANULARITY_OPTIONS.map((item) => (
                            <button
                                key={item.key}
                                type="button"
                                className={`admin-lang-tabs__item${granularity === item.key ? ' admin-lang-tabs__item--active' : ''}`}
                                onClick={() => setGranularity(item.key)}
                            >
                                {LangString(item.label)}
                            </button>
                        ))}
                    </div>
                </div>
                {error ? <div className="admin-empty">{error}</div> : null}
            </AdminSection>

            {!data ? (
                <AdminSection>
                    <div className="admin-empty">{loading ? LangString('AdminAnalyticsLoading') : LangString('AdminAnalyticsEmpty')}</div>
                </AdminSection>
            ) : (
                <>
                    <AdminGrid columns={4}>
                        <AdminStatCard
                            label={LangString('AdminClients')}
                            value={compactNumber(data.summary.usersTotal)}
                            action={
                                <div className="admin-stat-card__hint">
                                    {LangString('AdminClientsNew', data.summary.usersNew)}
                                    <AdminDelta
                                        current={data.summary.usersNew}
                                        previous={compare?.usersNew}
                                    />
                                </div>
                            }
                        />
                        <AdminStatCard
                            label={LangString('AdminWithOrders')}
                            value={`${data.summary.usersWithOrders}`}
                            action={
                                <div className="admin-stat-card__hint">
                                    {LangString(
                                        'AdminOrdersPerClientHint',
                                        data.summary.avgOrdersPerUser,
                                        data.summary.usersPaying,
                                    )}
                                </div>
                            }
                        />
                        <AdminStatCard
                            label={LangString('AdminOrders')}
                            tone="ok"
                            value={compactNumber(data.summary.ordersActive)}
                            action={
                                <div className="admin-stat-card__hint">
                                    {LangString(
                                        'AdminOrdersHint',
                                        data.summary.ordersTotal,
                                        data.summary.ordersNew,
                                    )}
                                    <AdminDelta
                                        current={data.summary.ordersNew}
                                        previous={compare?.ordersNew}
                                    />
                                </div>
                            }
                        />
                        <AdminStatCard
                            label={LangString('AdminTopups')}
                            tone="ok"
                            value={money(data.summary.paymentsUsd)}
                            action={
                                <div className="admin-stat-card__hint">
                                    {LangString(
                                        'AdminTopupsHint',
                                        data.summary.paymentsCount,
                                        data.summary.paymentsUsers,
                                    )}
                                    <AdminDelta
                                        current={data.summary.paymentsUsd}
                                        previous={compare?.paymentsUsd}
                                    />
                                </div>
                            }
                        />
                    </AdminGrid>

                    <AdminGrid columns={4}>
                        <AdminStatCard
                            label={LangString('AdminBilledLifetime')}
                            value={money(data.summary.billedUsd)}
                            action={
                                <div className="admin-stat-card__hint">
                                    {LangString('AdminBilledNewOrders', money(data.summary.billedUsdNew))}
                                </div>
                            }
                        />
                        <AdminStatCard
                            label={LangString('AdminEstMrr')}
                            value={money(data.summary.mrrUsd)}
                            action={
                                <div className="admin-stat-card__hint">
                                    {LangString('AdminEstMrrHint', data.summary.ordersLifetime)}
                                </div>
                            }
                        />
                        <AdminStatCard
                            label={LangString('AdminHeldBalance')}
                            value={money(data.summary.balanceHeldUsd)}
                        />
                        <AdminStatCard
                            label={LangString('AdminLifetimeOrders')}
                            value={compactNumber(data.summary.ordersLifetime)}
                            action={
                                <div className="admin-stat-card__hint">
                                    {LangString(
                                        'AdminLifetimeOrdersHint',
                                        LangString(
                                            data.range.granularity === 'week'
                                                ? 'AdminGranularityWeek'
                                                : data.range.granularity === 'month'
                                                  ? 'AdminGranularityMonth'
                                                  : 'AdminGranularityDay',
                                        ),
                                    )}
                                </div>
                            }
                        />
                    </AdminGrid>

                    <AdminCard title={LangString('AdminChartClientsOrders')}>
                        <AdminLineChart
                            series={[
                                {
                                    key: 'users',
                                    label: LangString('AdminChartNewClients'),
                                    color: CHART_COLORS[0],
                                    points: data.series.users,
                                },
                                {
                                    key: 'orders',
                                    label: LangString('AdminChartNewOrders'),
                                    color: CHART_COLORS[1],
                                    points: data.series.orders,
                                },
                                {
                                    key: 'pays',
                                    label: LangString('AdminTopups'),
                                    color: CHART_COLORS[3],
                                    points: data.series.paymentsCount,
                                },
                            ]}
                            formatX={formatX}
                        />
                    </AdminCard>

                    <AdminCard title={LangString('AdminChartTopupsUsd')}>
                        <AdminLineChart
                            series={[
                                {
                                    key: 'usd',
                                    label: LangString('AdminChartTopupsUsdLabel'),
                                    color: CHART_COLORS[2],
                                    points: data.series.paymentsUsd,
                                },
                            ]}
                            formatX={formatX}
                            formatY={(value) => `$${compactNumber(value)}`}
                        />
                    </AdminCard>

                    <AdminGrid columns={2}>
                        <AdminCard title={LangString('AdminRevenueByProduct')}>
                            <AdminHBarList
                                items={data.products.map((row, index) => ({
                                    key: String(row.type),
                                    label: productName(String(row.type)),
                                    value:
                                        row.billedUsd > 0
                                            ? row.billedUsd
                                            : row.ordersTotal,
                                    display:
                                        row.billedUsd > 0
                                            ? money(row.billedUsd)
                                            : LangString('AdminOrdersCount', row.ordersTotal),
                                    color: CHART_COLORS[index % CHART_COLORS.length],
                                }))}
                            />
                        </AdminCard>
                        <AdminCard title={LangString('AdminTopupsByMethod')}>
                            <AdminDonutChart
                                items={data.paymentsByMethod.map((row, index) => ({
                                    key: String(row.type),
                                    label: paymentMethodName(String(row.type)),
                                    value: row.usd,
                                    color: CHART_COLORS[index % CHART_COLORS.length],
                                }))}
                                formatValue={money}
                            />
                        </AdminCard>
                    </AdminGrid>

                    <AdminGrid columns={2}>
                        <AdminCard title={LangString('AdminOrdersPerClient')}>
                            <AdminHBarList
                                items={data.ordersPerUser.map((row, index) => ({
                                    key: row.bucket,
                                    label: LangString('AdminOrdersBucket', row.bucket),
                                    value: row.users,
                                    display: LangString('AdminClientsCount', row.users),
                                    color: CHART_COLORS[index % CHART_COLORS.length],
                                }))}
                            />
                        </AdminCard>
                        <AdminCard title={LangString('AdminTopupsByCurrency')}>
                            <AdminDonutChart
                                items={data.paymentsByCurrency.map((row, index) => ({
                                    key: String(row.currency),
                                    label: String(row.currency),
                                    value: row.usd,
                                    color: CHART_COLORS[(index + 2) % CHART_COLORS.length],
                                }))}
                                formatValue={money}
                            />
                        </AdminCard>
                    </AdminGrid>

                    <AdminCard title={LangString('AdminProducts')}>
                        <AdminDataTable
                            data={data.products}
                            rowKey={(row) => String(row.type)}
                            emptyMessage={LangString('AdminNoProductOrders')}
                            columns={[
                                {
                                    key: 'type',
                                    label: LangString('AdminProduct'),
                                    primary: true,
                                    render: (row) => productName(String(row.type)),
                                },
                                {
                                    key: 'ordersActive',
                                    label: LangString('AdminActive'),
                                    render: (row) => row.ordersActive,
                                },
                                {
                                    key: 'ordersTotal',
                                    label: LangString('AdminTotal'),
                                    render: (row) => row.ordersTotal,
                                },
                                {
                                    key: 'ordersNew',
                                    label: LangString('AdminNew'),
                                    render: (row) => row.ordersNew,
                                },
                                {
                                    key: 'users',
                                    label: LangString('AdminClients'),
                                    render: (row) => row.users,
                                },
                                {
                                    key: 'billedUsd',
                                    label: LangString('AdminBilledLifetime'),
                                    render: (row) => money(row.billedUsd),
                                },
                                {
                                    key: 'billedUsdNew',
                                    label: LangString('AdminNewBilled'),
                                    render: (row) => money(row.billedUsdNew),
                                },
                                {
                                    key: 'mrrUsd',
                                    label: LangString('AdminMrr'),
                                    render: (row) => money(row.mrrUsd),
                                },
                            ]}
                        />
                    </AdminCard>

                    <AdminCard title={LangString('AdminTopClients')}>
                        <AdminDataTable
                            data={data.topUsers}
                            rowKey={(row) => row.id}
                            emptyMessage={LangString('AdminNoClientActivity')}
                            columns={[
                                {
                                    key: 'id',
                                    label: LangString('AdminId'),
                                    primary: true,
                                    render: (row) => `#${row.id}`,
                                },
                                {
                                    key: 'name',
                                    label: LangString('AdminUser'),
                                    render: (row) => row.name,
                                },
                                {
                                    key: 'paymentsUsd',
                                    label: LangString('AdminTopups'),
                                    render: (row) => money(row.paymentsUsd),
                                },
                                {
                                    key: 'spent',
                                    label: LangString('AdminSpent'),
                                    render: (row) => money(row.spent),
                                },
                                {
                                    key: 'orders',
                                    label: LangString('AdminOrders'),
                                    render: (row) => row.orders,
                                },
                            ]}
                        />
                    </AdminCard>

                    <AdminGrid columns={2}>
                        <AdminCard title={LangString('AdminAppDownloadsOs')}>
                            <AdminDonutChart
                                items={appDownloadOs.map((row, index) => ({
                                    key: row.platform,
                                    label: osLabel(row.platform),
                                    value: row.downloads,
                                    color: CHART_COLORS[index % CHART_COLORS.length],
                                }))}
                                formatValue={(value) => compactNumber(value)}
                                emptyMessage={LangString('AdminNoInstallerDownloads')}
                            />
                        </AdminCard>
                        <AdminCard title={LangString('AdminAppDownloadsBranch')}>
                            <AdminHBarList
                                items={appDownloadBranches.map((row, index) => ({
                                    key: row.branchId,
                                    label: branchLabel(row.branchId),
                                    value: row.downloads,
                                    display: compactNumber(row.downloads),
                                    color: CHART_COLORS[(index + 1) % CHART_COLORS.length],
                                }))}
                                emptyMessage={LangString('AdminNoInstallerDownloads')}
                            />
                        </AdminCard>
                    </AdminGrid>

                    <AdminCard
                        title={LangString(
                            'AdminAppDownloadsTitle',
                            compactNumber(data.appDownloadsTotal || 0),
                        )}
                    >
                        <AdminDataTable
                            data={data.appDownloads || []}
                            rowKey={(row) => `${row.branchId}:${row.platform}:${row.version}`}
                            emptyMessage={LangString('AdminNoAppDownloads')}
                            columns={[
                                {
                                    key: 'version',
                                    label: LangString('AdminVersion'),
                                    primary: true,
                                    render: (row) => row.version,
                                },
                                {
                                    key: 'branchId',
                                    label: LangString('AdminBranch'),
                                    render: (row) => branchLabel(row.branchId),
                                },
                                {
                                    key: 'platform',
                                    label: LangString('AdminOs'),
                                    render: (row) => osLabel(row.platform),
                                },
                                {
                                    key: 'downloads',
                                    label: LangString('AdminDownloads'),
                                    render: (row) => compactNumber(row.downloads),
                                },
                            ]}
                        />
                    </AdminCard>
                </>
            )}
        </AdminPageWrap>
    );
};
